import { AnyObject, CurdDataSetConfig, FormFieldType, ItemAttrListDataSet, Position, Sensor } from '../types';
import {
  calcDistanceNauticalMiles,
  durationToString,
  toDateFormat,
  toDateTimeFormat,
  toNumber,
  toTimeFormat
} from '@mri/functions';
import { PositionShowComponent } from '@mri/app/modules';
import { Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';

function badgeDeviceTimeClass ( element: Position )  {
  const prevElement = element.prev;
  if(!prevElement) return '';

  const startTime = new Date(element.deviceTime);
  const endTime = new Date(prevElement?.deviceTime || new Date());
  const duration = Math.abs(endTime.getTime() - startTime.getTime());

  if (duration > 1000 * 60 * 5) return 'badge text-warn-900 bg-warn-50';

  return 'badge text-primary-900 bg-primary-50';
}

function badgeDeviceTime(element: Position){
  const prevElement = element.prev;
  if(!prevElement?.deviceTime) return '';

  const classValue = badgeDeviceTimeClass(element);

  const startTime = new Date(element.deviceTime);
  const endTime = new Date(prevElement?.deviceTime);
  return `<span class="${classValue}">${durationToString(Math.abs(endTime.getTime() - startTime.getTime()))}</span>`;
}

function loadTravelMap(prop: ItemAttrListDataSet<Position>): Observable<AnyObject> | void {
  const { component, elements, selectedElements } = prop;
  if (!component?.map) return;
  if (!elements?.length) return;

  component?.centerMap((elements)?.map(e => e.geometric) || []);

  const genericResource = component.genericResourceSrv;

  const filter = component.filterValues || {};

  return genericResource
    .listElements<Position>('positions', {
      filter: {
        endDeviceTime: filter['endDeviceTime'],
        startDeviceTime: filter['startDeviceTime'],
        mobileId: filter['mobileId'],
        deviceId: filter['deviceId'],

      },
      paginate: {
        page: 1, size: 9999999, select: 'onlyCoordinates'
      }
    }).pipe(
      tap(response => {
        console.log('next loadTravelMap 2');

        const { objects } = response;

        const mapElements: Array<google.maps.Polyline | google.maps.Marker> = [];

        let positionStopped!: Position;
        let isStopped = false;

        objects?.forEach((element, index) => {
          const nextElement = objects[index + 1];

          if (!nextElement) return;

          const distance = calcDistanceNauticalMiles(element.geometric, nextElement.geometric);
          if (distance < 0.001 && !isStopped) {
            positionStopped = element;
            isStopped = true;
          }

          if (isStopped && (!nextElement || calcDistanceNauticalMiles(positionStopped.geometric, nextElement.geometric) >= 0.005)) {
            const duration = durationToString(Math.abs(new Date(positionStopped.deviceTime).getTime() - new Date(element.deviceTime).getTime()));

            const stoppedMarker = new google.maps.Marker({
              position: {
                lat: element.geometric.coordinates[1] as number,
                lng: element.geometric.coordinates[0] as number
              },
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 6,
                fillColor: '#ffcd27',
                fillOpacity: 1,
                strokeWeight: 3,
                strokeColor: '#3dbf2c'
              }
            });

            stoppedMarker.addListener('click', () => {
              component.openInfoWindow({
                headerContent: 'Parada',
                content: `El vehiculo estuvo detenido por ${duration}`
              }, stoppedMarker);

              if (!element?._id) return;

              component.scrollToElementId(element._id);
              component.indicateIds([element._id]);
            });

            mapElements.push(stoppedMarker);
            isStopped = false;
          }

          const flightPlanCoordinates = [
            { lat: element.geometric.coordinates[1] as number, lng: element.geometric.coordinates[0] as number },
            {
              lat: nextElement.geometric.coordinates[1] as number,
              lng: nextElement.geometric.coordinates[0] as number
            }
          ];

          const flightPath = new google.maps.Polyline({
            path: flightPlanCoordinates,
            geodesic: true,
            strokeColor: isStopped ? '#ffcd27' : '#3dbf2c',
            strokeOpacity: 1,
            strokeWeight: 4
          });
          flightPath.addListener('click', () => {
            if (!element?._id) return;


            if(!component?.scrollToElementId(element._id)){
              component
                .reloadSync({ page: 1, size: 9999999 })
                .pipe(
                  tap(() => {
                    component?.scrollToElementId(element?._id || '');
                    component?.indicateIds([element?._id || '']);
                  })
                )
                .subscribe()
            }else {
              component?.indicateIds([element._id]);
            }
          });
          mapElements.push(flightPath);
        });


        component?.addMapElements('flightPath', [...mapElements]);

        if (!selectedElements?.length)
          component?.centerMap((objects)?.map(e => e.geometric) || []);
      })
    );
}

const formatTypeFieldMap: Record<string, FormFieldType> = {
  'string': FormFieldType.STRING,
  'number': FormFieldType.NUMBER,
  'boolean': FormFieldType.OPTIONS
};

export const positionDataSetConfig: CurdDataSetConfig<Position> = {
  entityName: 'Posicion',
  collectionName: 'Posicions',
  gatewayUrl: 'positions',
  baseBreadcrumb: 'positions',
  list: ({ genericSrcSrv }) => {

    return genericSrcSrv
      .getElements<Sensor>('sensors', { filter: {}, paginate: { size: 999, page: 1 } })
      .pipe(
        map(() => {
          return {
            pageConfig: {
              title: 'Reporte de posiciones',
              breadcrumb: [
                {
                  title: 'Reportes',
                  type: 'item'
                },
                {
                  title: 'Posiciones',
                  type: 'item',
                  link: '/reports/positions/list',
                  pageName: 'Reporte de posiciones'
                }
              ]
            },
            saverFilterExclude: ['startDeviceTime', 'endDeviceTime', 'mobileId', 'deviceId'],
            table: {
              columns: [
                {
                  key: 'deviceTime', label: 'Momento',
                  getValue: ({ element }) => {
                    const body = toDateTimeFormat(element.deviceTime);
                    const badge = badgeDeviceTime(element);

                    return `${body} ${badge}`;
                  },
                  sort: {
                    id: 'deviceTime',
                    fieldKey: 'deviceTime'
                  }
                  /*getMiddleValue: ({ element }) => {
                    const prevElement = element.prev;
                    if(!prevElement) return null;

                    const startTime = new Date(element.deviceTime);
                    const endTime = new Date(prevElement?.deviceTime || new Date());
                    return durationToString(Math.abs(endTime.getTime() - startTime.getTime()));
                  },
                  getMiddleClass: ({ element }) => {
                    const prevElement = element.prev;
                    if(!prevElement) return '';

                    const startTime = new Date(element.deviceTime);
                    const endTime = new Date(prevElement?.deviceTime || new Date());
                    const duration = Math.abs(endTime.getTime() - startTime.getTime());

                    if (duration > 1000 * 60 * 5) return 'text-warn-900 bg-warn-50';

                    return 'text-primary-900 bg-primary-50';
                  }*/
                },
                {
                  key: 'speed', label: 'Velocdad',
                  sort: {
                    id: 'speed',
                    fieldKey: 'speed'
                  }
                },
                {
                  key: 'odometerDay', label: 'Odometro',
                  getValue: ({ element }) => (element.properties || {})['odometerDay']
                },
                {
                  key: 'distance', label: 'Distancia',
                  getValue: ({ element }) => (element.properties || {})['distance'],
                  sort: {
                    id: 'distance',
                    fieldKey: 'distance'
                  }
                },
                {
                  key: 'ignition', label: 'Ignicion',
                  getValue: ({ element }) => {
                    if ((element.sensors || {})['ignition'])
                      return '<span class="text-success-500"> Encendido </span>';
                    return '<span class="text-warn-300"> Apagado </span>';
                  },
                  sort: {
                    id: 'sensor.ignition',
                    fieldKey: 'sensor.ignition'
                  }
                },
                {
                  key: 'address', label: 'Direccion',
                  getValue({ element }) {
                    const address = element.address || 'No definido';

                    return `<span class="text-expansible">${address}</span>`;
                  },
                  sort: {
                    id: 'address',
                    fieldKey: 'address'
                  }
                }
              ],
              noInitLoad: true,
              modeFilter: 'top',
              onMapReady: prop => loadTravelMap(prop),
              onLoadElements: prop => loadTravelMap(prop),
              onSelectedElements: ({ selectedElements, component }) => {

                if (!component?.map) return;
                const markers = (selectedElements || [])
                  .map((element) => {
                    return new google.maps.Marker({
                      position: {
                        lat: element.geometric.coordinates[1] as number,
                        lng: element.geometric.coordinates[0] as number
                      },
                      icon: {
                        path: 'm197.058594 257.113281-186.9375-69.761719 430.617187-177.351562-171.359375 433.242188zm0 0',
                        fillColor: 'rgb(27, 139, 249)',
                        fillOpacity: 1,
                        scale: 0.06,
                        strokeColor: 'rgba(33, 33, 33, 0.6)',
                        strokeWeight: 2,
                        anchor: new google.maps.Point(265, 200),
                        rotation: element.course || 0
                      }
                    });
                  });

                component?.addMapElements('selected', markers);

                if (selectedElements?.length)
                  component?.centerMap(selectedElements?.map(e => e.geometric) || []);
              }
            },
            card: {
              structure: {
                title: ({ element }) => {
                  const iconPower = `<i class="fa-solid  ${(element.sensors || {})['ignition'] ? 'fa-power-off text-success-500' : 'fa-solid fa-circle-notch text-muted '}"></i>`;
                  const iconSpeed = element.speed > 1 ?
                    `<span class="badge bg-primary-50 text-primary-700"><i class="fi fi-bs-tachometer text-primary-500"></i> ${element.speed.toFixed(2)}</span>` : '';
                  const [address0] = element.address?.split(',') ?? 'No definido';
                  return ` ${iconPower} ${[address0].filter(Boolean).join(', ')} ${iconSpeed}`;
                },
                body: ({ element }) => {
                  const [, address0, address1] = element?.address?.split(',') ?? 'No definido';

                  const odometerElement = element.speed > 1?
                    ` <div>
                        <span class="badge">Odometro: ${toNumber((element.properties || {})['odometerDay']).toFixed(2)}</span>
                      </div>
                    `: ''
                  return [
                    {
                      type: 'html',
                      value: `
                <div class="m-l-10">
                  <p>${[address0, address1].join(', ')}</p>
                  
                  ${odometerElement}
                  <div>
                    <span class="badge"> <i class="fi fi-br-calendar"></i> ${toDateFormat(element.deviceTime)}</span>
                    <span class="badge"> <i class="fi fi-br-clock"></i> ${toTimeFormat(element.deviceTime)}</span>
                  </div>
                </div>
                `
                    }
                  ];
                }
              }
            },
            actionsKeys: ['delete', 'export-xls'],
            extraActions: [
              {
                key: 'info',
                label: 'Informacion',
                icon: 'fi fi-rr-eye',
                click: ({ selectedElements, component }) => {
                  const element = selectedElements?.[0];
                  if (element === undefined) return;

                  component?.openDrawer(
                    'Informacion de Posicion',
                    PositionShowComponent,
                    { elementId: element._id }
                  );
                },
                inRow: true,
                disableWhen: ({ selectedElements }) => selectedElements?.length !== 1
              }
            ],
            filter: [
              {
                key: 'deviceId',
                label: 'Vehiculo o Dispositivo',
                type: FormFieldType.OPTIONS,
                optionsGateway: 'mobiles/currents',
                containerClass: 'col-12 col-md-3',
                filterOptions: {
                  '$valueSelected': 'deviceId'
                }
              },
              {
                key: 'startDeviceTime',
                label: 'Hora y fecha inicial',
                type: FormFieldType.DATE_TIME,
                containerClass: 'col-12 col-md-3',
                fillDefaultValue: () => new Date(toDateFormat(new Date()) + 'T00:00:00').toJSON()
              },
              {
                key: 'endDeviceTime',
                label: 'Hora y fecha final',
                type: FormFieldType.DATE_TIME,
                containerClass: 'col-12 col-md-3',
                fillDefaultValue: () => new Date(toDateFormat(new Date()) + 'T23:59:59').toJSON()
              }
            ]
          };
        })
      );

  },
  pages: ['list']
};
